/**
 * Navigation. Phones: a bottom tab bar (Trips, Shop, Recipes, Foods, More), where More opens a
 * sheet with the rest. ≥ 960px: the same as a side nav, with the rest listed under a divider.
 */
import type { ReactNode } from 'react'
import { useState } from 'react'
import type { NavSection, Route } from '../router'
import { href, navSectionOf } from '../router'
import {
  BasketIcon,
  FoodIcon,
  GearIcon,
  MapIcon,
  MoreIcon,
  PotIcon,
  RidgeIcon,
  Sheet,
  UploadIcon,
} from '../ui'

interface NavItem {
  section: NavSection
  label: string
  to: string
  icon: ReactNode
}

export const MAIN_NAV: NavItem[] = [
  { section: 'trips', label: 'Trips', to: href({ name: 'trips' }), icon: <MapIcon /> },
  { section: 'shop', label: 'Shop', to: href({ name: 'shop' }), icon: <BasketIcon /> },
  { section: 'recipes', label: 'Recipes', to: href({ name: 'recipes' }), icon: <PotIcon /> },
  { section: 'foods', label: 'Foods', to: href({ name: 'foods' }), icon: <FoodIcon /> },
]

/** Under "More" on phones; listed in full on the side nav. */
export const MORE_NAV: { name: Route['name']; label: string; to: string; icon: ReactNode }[] = [
  {
    name: 'availability',
    label: 'Availability',
    to: href({ name: 'availability' }),
    icon: <GridDots />,
  },
  {
    name: 'food-import',
    label: 'Import foods',
    to: href({ name: 'food-import' }),
    icon: <UploadIcon />,
  },
  { name: 'settings', label: 'Settings', to: href({ name: 'settings' }), icon: <GearIcon /> },
]

function GridDots() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {[5, 12, 19].flatMap((y) =>
        [5, 12, 19].map((x) => (
          <circle
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            r={x + y > 24 ? 2.4 : 1.6}
            fill="currentColor"
            opacity={x + y > 24 ? 1 : 0.5}
          />
        )),
      )}
    </svg>
  )
}

export function AppNav({ route }: { route: Route }) {
  const [moreOpen, setMoreOpen] = useState(false)
  const current = navSectionOf(route)

  return (
    <nav className="rs-nav" aria-label="Main">
      <a className="rs-nav__brand" href={href({ name: 'trips' })}>
        <RidgeIcon size={26} />
        <span>
          Resupport
          <span className="rs-nav__tagline">support for your resupply</span>
        </span>
      </a>
      <ul className="rs-nav__list">
        {MAIN_NAV.map((item) => (
          <li key={item.section}>
            <a
              className="rs-nav__item"
              href={item.to}
              aria-current={current === item.section ? 'page' : undefined}
            >
              <span className="rs-nav__icon">{item.icon}</span>
              {item.label}
            </a>
          </li>
        ))}
        <li className="rs-nav__more">
          <button
            type="button"
            className="rs-nav__item"
            aria-current={current === 'more' ? 'page' : undefined}
            aria-haspopup="dialog"
            onClick={() => setMoreOpen(true)}
          >
            <span className="rs-nav__icon">
              <MoreIcon />
            </span>
            More
          </button>
        </li>
      </ul>
      <div className="rs-nav__divider" role="presentation" />
      <ul className="rs-nav__list rs-nav__extra">
        {MORE_NAV.map((item) => (
          <li key={item.name}>
            <a
              className="rs-nav__item"
              href={item.to}
              aria-current={route.name === item.name ? 'page' : undefined}
            >
              <span className="rs-nav__icon">{item.icon}</span>
              {item.label}
            </a>
          </li>
        ))}
      </ul>

      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <ul className="rs-menu">
          {MORE_NAV.map((item) => (
            <li key={item.name}>
              <a href={item.to} onClick={() => setMoreOpen(false)}>
                {item.icon}
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </Sheet>
    </nav>
  )
}
